import React from 'react';
import InfoTooltip from '../../../components/InfoTooltip';

export interface ReportSummaryItem {
  label: string;
  value: React.ReactNode;
  /** Text colour for the value (e.g. red for a loss) */
  color?: string;
  /** Larger value, for the one figure the card is about */
  emphasis?: boolean;
  /** Muted value, e.g. "Not set" */
  muted?: boolean;
}

interface ReportSummaryCardProps {
  title: string;
  /** Bootstrap icon class, e.g. 'bi-cash-coin' */
  icon?: string;
  tooltip?: React.ReactNode;
  tooltipId?: string;
  items: ReportSummaryItem[];
}

/**
 * The summary strip shown above a report's table: a titled card of headline figures, each a small
 * uppercase label over its value. One component so every report's summary looks the same.
 */
const ReportSummaryCard: React.FC<ReportSummaryCardProps> = ({ title, icon, tooltip, tooltipId, items }) => (
  <div className="tw-mb-3 tw-rounded tw-border tw-border-line tw-bg-surface report-summary-card">
    <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2">
      <h6 className="tw-mb-0">
        {icon && <i className={`bi ${icon} tw-mr-2 tw-text-primary`}></i>}
        {title}
      </h6>
      {tooltip && (
        <InfoTooltip content={tooltip} id={tooltipId ?? `${title}-summary-tooltip`} placement="left">
          <span className="tw-text-content-muted" style={{ fontSize: '0.85rem', cursor: 'help' }}>
            <i className="bi bi-info-circle"></i>
          </span>
        </InfoTooltip>
      )}
    </div>
    <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-p-3 tw-text-center sm:tw-grid-cols-3 lg:tw-grid-cols-6">
      {items.map((item) => (
        <div key={item.label} className="tw-min-w-0">
          <div className="tw-text-xs tw-uppercase tw-text-content-muted">{item.label}</div>
          <div
            className={`${item.emphasis ? 'tw-text-2xl tw-font-bold' : 'tw-text-lg tw-font-semibold'} tw-tabular-nums ${
              item.muted ? 'tw-text-content-muted' : 'tw-text-content'
            }`}
            style={item.color ? { color: item.color } : undefined}
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default ReportSummaryCard;
